import { ViewHook } from "phoenix_live_view";
import { computePosition, autoUpdate, shift, size, flip } from "@floating-ui/dom";

const tooltipContentMaxHeight = 500;
const tooltipContentMaxWidth = 384;

class Tooltip extends ViewHook {
  private trigger?: HTMLElement;
  private content?: HTMLElement;

  private cleanup?: () => void;

  mounted() {
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    const contentId = this.el.dataset.tooltipContentId;

    if (!contentId) {
      throw new Error("Tooltip content ID not found");
    }

    this.trigger = this.el.querySelector("[data-tooltip-trigger]") as HTMLElement;
    this.content = document.querySelector(`#${contentId}`) as HTMLElement;

    if (!this.trigger || !this.content) {
      throw new Error("Tooltip trigger or content not found");
    }

    this.trigger.addEventListener("mouseenter", this.show);
    this.trigger.addEventListener("mouseleave", this.hide);
    this.trigger.addEventListener("focus", this.show);
    this.trigger.addEventListener("blur", this.hide);
  }

  destroyed() {
    this.trigger?.removeEventListener("mouseenter", this.show);
    this.trigger?.removeEventListener("mouseleave", this.hide);
    this.trigger?.removeEventListener("focus", this.show);
    this.trigger?.removeEventListener("blur", this.hide);

    this.hide();
  }

  private show() {
    const { trigger, content } = this;

    if (!trigger || !content) {
      return;
    }

    this.cleanup = autoUpdate(trigger, content, () => {
      void computePosition(trigger, content, {
        placement: "top",
        strategy: "fixed",
        middleware: [
          shift(),
          flip(),
          size({
            apply({ availableWidth, availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                maxWidth: `${Math.min(tooltipContentMaxWidth, Math.max(0, availableWidth))}px`,
                maxHeight: `${Math.min(tooltipContentMaxHeight, Math.max(0, availableHeight))}px`,
              });
            },
          }),
        ],
      }).then(({ x, y, placement }) => {
        Object.assign(content.style, {
          left: `${x}px`,
          top: `${y}px`,
        });

        const [side] = placement.split("-");

        content.dataset.side = side;
        content.dataset.state = "shown";

        if (content.hasAttribute("hidden")) {
          content.removeAttribute("hidden");
        }
      });
    });
  }

  private hide() {
    this.cleanup?.();

    const { content } = this;
    if (!content) {
      return;
    }

    content.dataset.state = "hidden";

    requestAnimationFrame(() => {
      // The animation runs for 150ms. Wait for a period just below that
      // to ensure the animation is complete.
      setTimeout(() => {
        content.setAttribute("hidden", "");
      }, 130);
    });
  }
}

export default Tooltip;
